import { Component, EventEmitter, OnInit, Output, ViewChild, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Issue } from '@models/issue.model';
import { Line } from '@models/line.model';
import { Reason, ReasonScope } from '@models/reason.model';

import { LineService } from '@services/line.service';
import { ReasonsService } from '@services/reasons.service';

import { Log } from 'ng2-logger';

import * as _ from 'lodash';
import { PageStepperComponent } from '@app/shared/components';
import { Tool } from '@app/tool/model/tool.model';
import { AppUtils } from '@app/shared/util/app.utils';
import { TreeModel } from '@app/shared/components/tree/tree.model';

enum IssueReturnSteps {
    SELECT_LINE = 1,
    SCAN_TOOL = 2,
    ISSUE_DETAILS = 3
}

declare const $: any;

@Component({
    selector: 'app-signal-issue-form',
    templateUrl: './signal-issue-form.component.html',
    styleUrls: ['./signal-issue-form.component.scss']
})
export class SignalIssueFormComponent implements OnInit {

    _logger = Log.create(SignalIssueFormComponent.name);

    @ViewChild(PageStepperComponent, {static: true}) pageStepper: PageStepperComponent;

    @Output() signal: EventEmitter<any> = new EventEmitter();

    signalIssueForm: UntypedFormGroup;
    firstNextTried = false;
    public issue: Issue = new Issue();
    selectedTools: Tool[] = [];

    // Array that keeps the lines which can be selected for a issue
    lines: Line[] = [];

    // Array that keeps the reasons which can be selected for a issue
    reasons: Reason[] = [];

    selectToolsForm: UntypedFormGroup;
    @Input() isLoading: boolean;

    static selectValidator(control: UntypedFormControl): { [s: string]: boolean } {
        if (control.value === 0) {
            return {'required': true};
        }
        return null;
    }

    constructor(private lineService: LineService,
                private reasonsService: ReasonsService) {
    }

    ngOnInit() {
        this.onFormInit();
        this.getLines();
        this.getReasons();
    }

    onStepChange(stepIndex) {
        switch (stepIndex) {
            case IssueReturnSteps.SELECT_LINE:
                if (this.signalIssueForm.controls['lineId'].invalid) {
                    return;
                }
                break;
            case IssueReturnSteps.SCAN_TOOL:
                if (this.selectToolsForm.controls.selectedTools.valid) {
                    return;
                }
                break;
            case IssueReturnSteps.ISSUE_DETAILS:
                if (this.signalIssueForm.invalid) {
                    return;
                }
                break;
            default:
                break;
        }
    }

    get isLineSelected() {
        return this.signalIssueForm.controls['lineId'].valid;
    }

    get isToolScanned() {
        return this.selectToolsForm.controls.selectedTools.valid;
    }

    get isFormValid() {
        return this.signalIssueForm.valid;
    }

    onTryNext() {
        this.firstNextTried = true;
    }

    onFormInit() {
        if (!this.selectToolsForm) {
            this.selectToolsForm = new UntypedFormGroup({
                'id': new UntypedFormControl(1),
                'selectedTools': new UntypedFormControl([], [AppUtils.atLeastOneToolValidator(1)])
            });
        }
        this.issue.isReplacementOrdered = true;
        this.signalIssueForm = new UntypedFormGroup({
            'lineId': new UntypedFormControl(this.issue.line.id, [Validators.required, SignalIssueFormComponent.selectValidator.bind(this)]),
            'reasonId': new UntypedFormControl(this.issue.reason.id, [Validators.required, SignalIssueFormComponent.selectValidator.bind(this)]),
            'description': new UntypedFormControl(this.issue.description, Validators.required),
            'isReplacementOrdered': new UntypedFormControl(this.issue.isReplacementOrdered, Validators.required)
        });

        this._logger.info('onFormInit:: form initialised', this.signalIssueForm);
    }

    signalIssue() {
        if (this.signalIssueForm.invalid || this.selectToolsForm.controls.selectedTools.invalid) {
            this._logger.info('signalIssue:: Form is invalid', this.signalIssueForm);
            return;
        }

        this._logger.info('signalIssue:: Form is valid', this.signalIssueForm);

        this.selectedTools = this.selectToolsForm.controls.selectedTools.value;
        this.issue.tools = this.adaptTools([...this.selectToolsForm.controls.selectedTools.value]);

        this.signal.emit(this.issue);
    }

    adaptTools(selectedTools) {
        _.remove(selectedTools, (someObj: TreeModel) => {
            return someObj.data && someObj.data.length > 1 && someObj.data.id == null;
        });

        return _.map(selectedTools, 'data.id')
            .filter((value, index, self) => self.indexOf(value) === index)
            .map(tool => new Tool({id: tool}));
    }

    private getLines() {
        // Observable for retrieving operation types data
        this.lineService.getAllLines().subscribe(
            (data: any) => {
                this.lines = data.items;
            },
            (error: any) => {
            }
        );
    }

    private getReasons() {
        // Observable for retrieving operation types data
        this.reasonsService.getAllIReasons(null, ReasonScope.ToolIssue).subscribe(
            (data: any) => {
                this.reasons = data.items;
            },
            (error: any) => {
            }
        );
    }

    onLineChange(line) {
        if (this.selectToolsForm && this.issue?.line?.id !== line?.id) {
            this.selectedTools = [];
            this.selectToolsForm.controls['selectedTools'].setValue([]);
        }

        this.firstNextTried = false;

        this.issue.line = line;
    }

    onReasonChange(reason) {
        this.issue.reason = reason;
    }

    get isDisable() {
        return this.selectToolsForm.controls.selectedTools.value.filter(item => item.data.excludedFromReturn === true).length > 0;
    }

    get activeStep() {
        return this.pageStepper ? this.pageStepper.activeStepIndex : 0;
    }
}
